// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { getCanonicalUrl } from '../../utils/get-canonical-url';
import { getLocale } from '../../utils/locale';

const getRobotMetaTag = ({ noIndex, noFollow }) => {
	let metaTags = [];

	if (noIndex) {
		metaTags = ['noindex'];
	} else {
		metaTags = ['index'];
	}

	if (noFollow) {
		metaTags = [...metaTags, 'nofollow'];
	} else {
		metaTags = [...metaTags, 'follow'];
	}

	if (noIndex || noFollow) {
		return <meta name="robots" content={metaTags.join(',')} />;
	}
	return <meta name="robots" content={metaTags.join(',')} />;
};

export const Head = ({ metaContent, seoSchemas }) => {
	const { pathname } = useLocation();
	const locale = getLocale(pathname);
	const [gtmID, setGtmID] = React.useState();
	const [canonicalUrlCA, setCanonicalUrlCA] = useState('');

	React.useLayoutEffect(() => {
		if (locale === 'ie') {
			setGtmID(process.env.GATSBY_GTM_IE_TRACKING_ID);
		} else if (locale === 'au') {
			setGtmID(process.env.GATSBY_GTM_AU_TRACKING_ID || 'GTM-P9BG9LGG');
		} else if (locale === 'ca') {
			setGtmID(process.env.GATSBY_GTM_CA_TRACKING_ID);
		} else {
			setGtmID(process.env.GATSBY_GTM_TRACKING_ID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const data = useStaticQuery(
		graphql`
			query {
				datoCmsSiteSpecificSetting {
					defaultSeoInformation {
						title
						description
						image {
							url
						}
						twitterCard
					}
				}
				site {
					siteMetadata {
						hrefLang
					}
				}
			}
		`
	);
	const {
		title: defaultTitle,
		description: defaultDescription,
		image: defaultImage,
		twitterCard: defaultTwitterCard,
	} = data.datoCmsSiteSpecificSetting?.defaultSeoInformation;
	// const defaultLang = data.site?.siteMetadata?.hrefLang;

	const { noIndex, noFollow, metaInformation, canonicalUrl } = metaContent;

	useEffect(() => {
		if (locale === 'ca') {
			// Set canonical URL to avoid duplicates by
			// removing query parameters & fragments
			const resolvedCanonicalURL = getCanonicalUrl(canonicalUrl);
			setCanonicalUrlCA(resolvedCanonicalURL);
		}
	}, [canonicalUrl, locale]);

	const metaLang = locale === '' ? 'en' : locale;

	let metaTitle = '';
	if (locale === 'ca') {
		metaTitle =
			metaInformation?.title ||
			(metaContent.fallbackTitle
				? `${metaContent.fallbackTitle} | Peninsula Canada`
				: defaultTitle);
	} else {
		metaTitle =
			metaInformation?.title ||
			(metaContent.fallbackTitle
				? `${metaContent.fallbackTitle} | Peninsula UK`
				: defaultTitle);
	}

	const metaDescription =
		metaInformation?.description ||
		metaContent?.fallbackDescription ||
		defaultDescription;
	const metaTwitterCard = metaInformation?.twitterCard || defaultTwitterCard;
	const metaImage = metaInformation?.image?.url || defaultImage?.url;

	return (
		<Helmet
			htmlAttributes={{
				lang: metaLang,
			}}
			title={metaTitle}
		>
			<script type="application/ld+json">
				{JSON.stringify(seoSchemas.organization)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(seoSchemas.breadcrumbs)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(seoSchemas.image)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(seoSchemas.faq)}
			</script>
			<script type="application/ld+json">
				{JSON.stringify(seoSchemas.localBusiness)}
			</script>
			{locale === 'ca' ? (
				<script type="application/ld+json">
					{JSON.stringify(seoSchemas.blog)}
				</script>
			) : null}
			{locale === 'ca' ? (
				<meta
					name="google-site-verification"
					content="HrTGgUGon1yJmx9vjdDZ7Ho9G6TNZUW1GEBgRRyTMrc"
				/>
			) : null}
			<meta name="description" content={metaDescription} />
			{getRobotMetaTag({ noIndex, noFollow })}
			<link
				rel="canonical"
				href={locale !== 'ca' ? canonicalUrl : canonicalUrlCA}
			/>
			<meta name="twitter:card" content={metaTwitterCard} />
			<meta property="og:title" content={metaTitle} />
			<meta property="og:description" content={metaDescription} />
			<meta property="og:image" content={metaImage} />
			<meta property="og:type" content="website" />
			<meta property="og:url" content={canonicalUrl} />
			{gtmID && locale !== 'ca' && (
				<script id="gtag-config">
					{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':

					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','${gtmID}');
				`}
				</script>
			)}

			{locale === '' ? (
				<link
					rel="preconnect"
					href="https://dev.visualwebsiteoptimizer.com"
				/>
			) : null}
			{locale === '' ? (
				<script id="vwoCode" type="text/javascript">
					{`
            window._vwo_code || (function() {
var account_id=1004401,
version=2.1,
settings_tolerance=2000,
hide_element='body',
hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
/* DO NOT EDIT BELOW THIS LINE */
f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};
try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},
library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},
hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},
getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');
    if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},
    finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);
    d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){w._vwo_code.addScript({text:o.responseText})}else{w._vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}w._vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();
           `}
				</script>
			) : null}
		</Helmet>
	);
};

Head.defaultProps = {
	metaContent: {
		noIndex: false,
		noFollow: false,
		metaInformation: {
			title: '',
			description: '',
			image: {
				url: '',
			},
			twitterCard: '',
		},
		canonicalUrl: '',
	},
};

Head.propTypes = {
	metaContent: PropTypes.shape({
		noIndex: PropTypes.bool,
		noFollow: PropTypes.bool,
		metaInformation: PropTypes.shape({
			title: PropTypes.string,
			description: PropTypes.string,
			image: PropTypes.shape({ url: PropTypes.string }),
			twitterCard: PropTypes.string,
		}),
		canonicalUrl: PropTypes.string,
	}),
};
